import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Bell, Briefcase, DollarSign, Star, MapPin, Clock, ArrowRight } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";

export default function ProfessionalHome() {
  const { user } = useAuth();
  const [pro, setPro] = useState(null);
  const [offers, setOffers] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const load = async () => {
    if (!user?.professional_id) return;
    const [p, o, a] = await Promise.all([
      api.get(`/professionals/${user.professional_id}`),
      api.get("/offers", { params: { status: "pending" } }),
      api.get("/assignments"),
    ]);
    setPro(p.data); setOffers(o.data);
    setAssignments(a.data.filter((x) => ["confirmed", "checked_in"].includes(x.status)));
  };
  useEffect(() => { load(); }, [user]);

  const toggleAvail = async (val) => {
    try {
      await api.patch(`/professionals/${pro.id}`, { available_now: val });
      setPro({ ...pro, available_now: val });
      toast.success(val ? "You're available for shifts now" : "Unavailable updated");
    } catch { toast.error("Failed"); }
  };

  if (!pro) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="pro-home">
      <div>
        <div className="text-xs text-slate-500">Welcome back,</div>
        <h1 className="text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>{pro.first_name} {pro.last_name}</h1>
        <p className="text-xs text-slate-500">{pro.primary_position}</p>
      </div>

      <Card className="bg-brand-navy text-white border-0">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold">Availability</div>
              <div className="mt-1 text-lg font-bold">{pro.available_now ? "Available Now" : "Not Available"}</div>
              <div className="text-xs text-white/60 mt-1">Toggle to receive immediate offers</div>
            </div>
            <Switch checked={pro.available_now} onCheckedChange={toggleAvail} data-testid="pro-available-toggle" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-2">
        <Card><CardContent className="p-3 text-center">
          <Star className="w-4 h-4 text-amber-500 mx-auto" />
          <div className="text-lg font-bold text-brand-navy mt-1">{pro.netstaff_score}</div>
          <div className="text-[10px] uppercase text-slate-500">Score</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <DollarSign className="w-4 h-4 text-green-600 mx-auto" />
          <div className="text-lg font-bold text-brand-navy mt-1">${pro.hourly_rate}</div>
          <div className="text-[10px] uppercase text-slate-500">Hr Rate</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <Briefcase className="w-4 h-4 text-brand-teal mx-auto" />
          <div className="text-lg font-bold text-brand-navy mt-1">{pro.experience_years}</div>
          <div className="text-[10px] uppercase text-slate-500">Yrs Exp</div>
        </CardContent></Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4 text-brand-teal" />Pending offers</h2>
          {offers.length > 0 && <Link to="/pro/offers" className="text-xs text-brand-teal font-semibold">See all <ArrowRight className="inline w-3 h-3" /></Link>}
        </div>
        {offers.slice(0, 2).map((o) => (
          <Card key={o.id} className="mb-2" data-testid={`pro-offer-${o.id}`}>
            <CardContent className="p-4">
              <div className="font-semibold">{o.request?.position}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 mt-1"><MapPin className="w-3 h-3" />{o.facility?.name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 mt-1"><Clock className="w-3 h-3" />{o.request?.shift_date} · {o.request?.start_time}-{o.request?.end_time}</div>
              <Link to="/pro/offers" className="inline-block mt-2 text-xs text-brand-teal font-semibold">Respond →</Link>
            </CardContent>
          </Card>
        ))}
        {offers.length === 0 && <div className="text-center text-sm text-slate-500 py-4">No pending offers</div>}
      </div>

      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2"><Briefcase className="w-4 h-4 text-brand-teal" />Upcoming shifts</h2>
        {assignments.slice(0, 3).map((a) => (
          <Card key={a.id} className="mb-2" data-testid={`pro-assignment-${a.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">{a.facility?.name}</div>
                <div className="text-xs text-slate-500">{a.request?.shift_date} · {a.request?.start_time}-{a.request?.end_time}</div>
              </div>
              <StatusPill status={a.status} />
            </CardContent>
          </Card>
        ))}
        {assignments.length === 0 && <div className="text-center text-sm text-slate-500 py-4">No upcoming shifts</div>}
      </div>
    </div>
  );
}
